import React, { Fragment } from 'react';
import pt from 'prop-types';
import cx from 'classnames';
import get from 'lodash/get';
import Overlay from 'components/overlay';
import StorefrontEditorIcon from 'components/storefront/editor/icon';
import { EasyblocksBackendContext } from 'components/storefront/editor/backendContext';
import { getBlocksToAdd } from 'components/storefront/editor/utils/overlay';
import {
  SWELL_GLOBAL,
  addSwellPagePrefix,
  getDocumentPage,
} from 'components/storefront/editor/utils/easyblocks';
import {
  postEasyblocksRemoveItems,
  postEasyblocksDuplicateItems,
  postEasyblocksToggleItem,
  postEasyblocksFocus,
} from 'components/storefront/editor/utils/post-message';

const ACTIONS = Object.freeze({
  duplicate: {
    label: 'Duplicate',
    icon: 'copy',
  },
  hide: {
    label: (item) => (item.disabled ? 'Show' : 'Hide'),
    svgIcon: 'eye',
    withPrefix: false,
  },
  delete: {
    label: (item) =>
      item._component.startsWith('Block') ? 'Delete block' : 'Delete section',
    icon: 'trash',
    className: 'danger',
  },
});

const DEFAULT_ACTIONS = Object.freeze([['duplicate'], ['delete']]);

export default class PageItemEllipsisOverlay extends React.PureComponent {
  static propTypes = {
    pageId: pt.string.isRequired,
    easyblocksConfig: pt.object.isRequired,
    editorConfig: pt.object.isRequired,
    data: pt.shape({
      domRect: pt.instanceOf(DOMRect).isRequired,
      path: pt.string.isRequired,
      overlayProps: pt.object,
    }),
    location: pt.object.isRequired,
    onDuplicateElement: pt.func.isRequired,
    onDeleteElement: pt.func.isRequired,
    onClose: pt.func.isRequired,
  };

  state = {
    document: null,
  };

  componentDidMount() {
    this.setDocument();
  }

  setDocument() {
    const { pageId } = this.props;
    const { easyblocksState } = this.context;

    this.setState({ document: getDocumentPage(pageId, easyblocksState) });
  }

  duplicate() {
    const { data, onClose } = this.props;
    const path = addSwellPagePrefix(data.path);

    postEasyblocksDuplicateItems(path);
    onClose();
  }

  hide() {
    const { data, onClose } = this.props;
    const path = addSwellPagePrefix(data.path);

    postEasyblocksToggleItem(path);
    postEasyblocksFocus(SWELL_GLOBAL);

    onClose();
  }

  delete() {
    const { data, onClose } = this.props;
    const path = addSwellPagePrefix(data.path);

    postEasyblocksRemoveItems(path);
    onClose();
  }

  onClick = (event) => {
    const { id } = event.currentTarget.dataset;

    switch (id) {
      case 'duplicate':
        return this.duplicate();
      case 'hide':
        return this.hide();
      case 'delete':
        return this.delete();
      default:
        return null;
    }
  };

  canDuplicateSection(item) {
    const { editorConfig, data } = this.props;
    const { document } = this.state;
    const { path } = data;
    const { allSections } = editorConfig;

    const sectionConfig = allSections.find(
      (section) => section.id === item._component,
    );

    if (!sectionConfig) {
      return false;
    }

    // do not duplicate sections without presets
    if (sectionConfig.presets?.length <= 0) {
      return false;
    }

    // do not allow to add more than 'limit' of same sections
    if (sectionConfig?.limit > 0) {
      const [sectionGroup] = path.split('.');
      const section = document[sectionGroup];
      if (section) {
        let count = 0;
        for (const existing of section) {
          if (existing?._component === item._component) {
            count += 1;

            if (count >= sectionConfig.limit) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  canDuplicateItem(item) {
    const { editorConfig, easyblocksConfig, data } = this.props;
    const { document } = this.state;
    const { path } = data;
    const [, , blockGroup] = path.split('.');

    // sections
    if (!blockGroup) {
      return this.canDuplicateSection(item);
    }

    const { blocks: blocksToAdd } = getBlocksToAdd(
      editorConfig,
      easyblocksConfig,
      document,
      path,
    );
    const hasBlocksToAdd = blocksToAdd?.length > 0;

    if (!hasBlocksToAdd) {
      return false;
    }

    return blocksToAdd.some((blockToAdd) => blockToAdd.id === item._component);
  }

  canDeleteItem(item) {
    const { editorConfig, data } = this.props;
    const { allSections } = editorConfig;
    const { path } = data;
    const [, , blockGroup] = path.split('.');

    // always allow delete blocks
    if (blockGroup) {
      return true;
    }

    const sectionConfig = allSections.find(
      (section) => section.id === item._component,
    );

    if (!sectionConfig) {
      return false;
    }

    // do not allow to delete sections without presets
    return sectionConfig.presets?.length > 0;
  }

  shouldRenderAction(action, itemConfig) {
    switch (action) {
      case 'duplicate':
        return this.canDuplicateItem(itemConfig);
      case 'delete':
        return this.canDeleteItem(itemConfig);
      default:
        return true;
    }
  }

  renderItem = (item) => {
    const { path } = this.props.data;
    const { document } = this.state;
    const itemConfig = get(document, path);
    const { id, icon, svgIcon, withPrefix, label, className } = item;

    if (!itemConfig) {
      return null;
    }

    if (!this.shouldRenderAction(id, itemConfig)) {
      return null;
    }

    return (
      <button
        key={id}
        data-id={id}
        className={cx('storefront-editor--overlay-item', className)}
        onClick={this.onClick}
        type="button"
      >
        <StorefrontEditorIcon
          type={icon}
          svgType={svgIcon}
          width={14}
          height={14}
          withPrefix={withPrefix}
        />
        {typeof label === 'function' ? label(itemConfig) : label}
      </button>
    );
  };

  renderItemGroup = (itemGroup, index, _itemGroups, result) => {
    const items = itemGroup.map(this.renderItem).filter(Boolean);
    const hasItems = items.length > 0;

    if (!hasItems) {
      return null;
    }

    const shouldRenderSeparator = result.length > 0;

    return (
      <Fragment key={index}>
        {shouldRenderSeparator && <hr />}
        <div className="storefront-editor--overlay-item-group">{items}</div>
      </Fragment>
    );
  };

  getActions() {
    return this.props.data.overlayProps?.actions || DEFAULT_ACTIONS;
  }

  getActionGroupConfigs(actionGroup) {
    return actionGroup.reduce((acc, action) => {
      const actionConfig = ACTIONS[action];

      if (actionConfig) {
        acc.push({ id: action, ...actionConfig });
      }

      return acc;
    }, []);
  }

  getItems() {
    const actionGroups = this.getActions();

    return actionGroups.reduce((acc, actionGroup) => {
      const actionGroupConfigs = this.getActionGroupConfigs(actionGroup);

      acc.push(actionGroupConfigs);

      return acc;
    }, []);
  }

  render() {
    const { data, onClose } = this.props;
    const { document } = this.state;

    if (!document) {
      return null;
    }

    return (
      <Overlay
        className="storefront-editor--overlay storefront-editor--overlay-ellipsis"
        domRect={data.domRect}
        items={this.getItems()}
        renderItem={this.renderItemGroup}
        onClose={onClose}
        search={false}
        {...data.overlayProps}
      />
    );
  }
}

PageItemEllipsisOverlay.contextType = EasyblocksBackendContext;
